import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Injectable, inject} from '@angular/core';
import {Observable, defer, finalize, firstValueFrom, from, map, shareReplay} from 'rxjs';
import {ApiResponse} from '../../auth/auth.models';
import {CatalogStorageService} from './catalog-storage.service';

export interface CatalogDefinition<T> {
  id: string;
  url: string;
  schema: number;
  headers?: Record<string, string>;
  isData: (value: unknown) => value is T;
}

interface CatalogRecord<T> {etag: string; data: T}

@Injectable({providedIn: 'root'})
export class CatalogCacheService {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(CatalogStorageService);
  private readonly pending = new Map<string, Observable<unknown>>();

  invalidate<T>(definition: CatalogDefinition<T>, language: string): void {
    const key = this.key(definition, language);
    this.pending.delete(key);
    // Also prevents an older in-flight response from writing its stale snapshot.
    this.storage.write(key, JSON.stringify({invalidated: crypto.randomUUID()}));
  }

  private key<T>(definition: CatalogDefinition<T>, language: string): string {
    return `invemory.catalog.${JSON.stringify([definition.id, definition.url, language.trim().toLowerCase(), definition.schema])}`;
  }

  get<T>(definition: CatalogDefinition<T>, language: string): Observable<T> {
    const locale = language.trim().toLowerCase();
    const key = this.key(definition, locale);
    return defer(() => {
      let request = this.pending.get(key) as Observable<T> | undefined;
      if (!request) {
        request = from(this.storage.withLock(key, () => this.revalidate(definition, locale, key))).pipe(
          finalize(() => { if (this.pending.get(key) === request) this.pending.delete(key); }),
          shareReplay({bufferSize: 1, refCount: false}),
        );
        this.pending.set(key, request);
      }
      // Each consumer owns its DTO copy; form changes must never mutate shared cache data.
      return request.pipe(map(data => structuredClone(data)));
    });
  }

  private async revalidate<T>(definition: CatalogDefinition<T>, language: string, key: string): Promise<T> {
    const original = this.storage.read(key);
    const cached = this.parse(original, definition);
    const record = await this.fetch(definition, language, cached);
    // Fallback for WebViews without Web Locks: do not overwrite another tab's newer write.
    if (this.storage.read(key) === original) this.storage.write(key, JSON.stringify(record));
    return record.data;
  }

  private async fetch<T>(definition: CatalogDefinition<T>, language: string, cached: CatalogRecord<T> | null): Promise<CatalogRecord<T>> {
    const headers: Record<string, string> = {'Accept-Language': language, ...definition.headers};
    if (cached) headers['If-None-Match'] = cached.etag;
    try {
      const response = await firstValueFrom(this.http.get<ApiResponse<T>>(definition.url, {headers, observe: 'response'}));
      if (response.status === 304) return this.notModified(cached);
      const etag = response.headers.get('ETag');
      const data = response.body?.data;
      if (!this.isETag(etag) || !definition.isData(data)) throw new Error('Invalid catalog response.');
      return {etag, data};
    } catch (error) {
      // Angular's XHR backend reports 304 as HttpErrorResponse; native/fetch may return a response.
      if (error instanceof HttpErrorResponse && error.status === 304) return this.notModified(cached);
      throw error;
    }
  }

  private notModified<T>(cached: CatalogRecord<T> | null): CatalogRecord<T> {
    if (cached) return cached;
    // An unconditional request cannot legitimately return 304; fail instead of looping forever.
    throw new Error('Catalog returned 304 without valid cached data.');
  }

  private parse<T>(raw: string | null, definition: CatalogDefinition<T>): CatalogRecord<T> | null {
    try {
      const record: unknown = JSON.parse(raw ?? 'null');
      if (!record || typeof record !== 'object' || !('etag' in record) || !('data' in record)) return null;
      return this.isETag(record.etag) && definition.isData(record.data) ? {etag: record.etag, data: record.data} : null;
    } catch { return null; }
  }

  private isETag(value: unknown): value is string {
    return typeof value === 'string' && /^(W\/)?"[^"\r\n]+"$/.test(value);
  }
}
